import {objectFilterEntries, objectGetKeys, objectPick, PickByValue, Values} from "@wix/devzai-utils-common";
import {
    WixPremiumBillingCycle,
    wixPremiumBooleanFeatureIsEnabled,
    WixPremiumFeaturesStatus,
    WixPremiumFeatureStatus,
    WixPremiumFeatureType, wixPremiumQuotaFeatureGetLimit,
    wixPremiumQuotaFeatureGetRemainedQuota
} from '@wix/devzai-common-wix';
import {DoppePremiumFeatures, DoppePremiumFeaturesSpec} from "./doppe-premium-features";

export type DoppePremiumOrderPageData = {
    userEmail: string;
    orderSessionId: string;
    riskCheckIframeUrl: string;
    eventAggregator: string;
}

export const DoppePremiumProductsTypeId = '9c7a270d-c9f2-41bf-bb56-e6543c6b0008';

export const DoppePremiumProducts = {
    Starter: '270041d1-81b3-4b07-be41-06fbd38edbad',
    Pro: '3a3b641e-270d-48ac-b9bf-e5ebfca5ee57',
    TestTier1: '3eb0990a-93e7-41db-8829-28ab32a105e3',
    TestTier2: '4fb355bd-8151-4df3-bec0-84ac6a9867ce',
    TestTier3: '84073c0c-cdaf-4169-a5d6-c8829971fada',
}

export type DoppePremiumProducts = Values<typeof DoppePremiumProducts>;

export const DoppePremiumBillingCycles = objectPick(WixPremiumBillingCycle, [
    'Monthly',
    'Yearly'
]);

export type DoppePremiumBillingCycles = Values<typeof DoppePremiumBillingCycles>;

export type DoppePremiumBooleanFeatures = keyof PickByValue<typeof DoppePremiumFeaturesSpec, typeof WixPremiumFeatureType.Boolean>
export type DoppePremiumQuotaFeatures = keyof PickByValue<typeof DoppePremiumFeaturesSpec, typeof WixPremiumFeatureType.Quota>

export type DoppePremiumFeaturesStatus = WixPremiumFeaturesStatus<typeof DoppePremiumFeaturesSpec>

export function doppePremiumFeatureIsEnabled<F extends DoppePremiumBooleanFeatures> (
    premiumFeaturesStatus: DoppePremiumFeaturesStatus | null,
    feature: F
) {
    if (premiumFeaturesStatus === null) {
        return true;
    }

    return wixPremiumBooleanFeatureIsEnabled(premiumFeaturesStatus[feature] as WixPremiumFeatureStatus.BooleanFeature);
}

export function doppePremiumFeatureIsDisabled<F extends DoppePremiumBooleanFeatures> (
    premiumFeaturesStatus: DoppePremiumFeaturesStatus | null,
    feature: F
) {
    return !doppePremiumFeatureIsEnabled(premiumFeaturesStatus, feature);
}

export function doppePremiumFeatureHasFiniteRemainedQuota<F extends DoppePremiumQuotaFeatures> (
    premiumFeaturesStatus: DoppePremiumFeaturesStatus | null,
    feature: F
) : boolean {
    return doppePremiumFeatureGetRemainedQuota(premiumFeaturesStatus, feature) < Infinity;
}

export function doppePremiumFeatureGetRemainedQuota<F extends DoppePremiumQuotaFeatures> (
    premiumFeaturesStatus: DoppePremiumFeaturesStatus | null,
    feature: F
) : number {
    if (premiumFeaturesStatus === null) {
        return Infinity;
    }

    return wixPremiumQuotaFeatureGetRemainedQuota(premiumFeaturesStatus[feature] as WixPremiumFeatureStatus.QuotaFeature);
}

export function doppePremiumFeatureGetLimit<F extends DoppePremiumQuotaFeatures> (
    premiumFeaturesStatus: DoppePremiumFeaturesStatus | null,
    feature: F,
    defaultLimit?: number
) : number {
    if (premiumFeaturesStatus === null) {
        return Infinity;
    }

    return wixPremiumQuotaFeatureGetLimit(premiumFeaturesStatus[feature] as WixPremiumFeatureStatus.QuotaFeature, defaultLimit);
}


export function doppePremiumGetBooleanFeatures () {
    return objectGetKeys(
        objectFilterEntries(DoppePremiumFeaturesSpec, featureType => featureType === WixPremiumFeatureType.Boolean)
    ) as DoppePremiumBooleanFeatures[];
}

export function doppePremiumGetQuotaFeatures () {
    return objectGetKeys(
        objectFilterEntries(DoppePremiumFeaturesSpec, featureType => featureType === WixPremiumFeatureType.Quota)
    ) as DoppePremiumQuotaFeatures[];
}

export function doppePremiumIsActive (featuresStatus: DoppePremiumFeaturesStatus) {
    // Important: We assume that the `hopp_remove_banner` feature is enabled if and only if the user has an active
    // premium plan.
    return doppePremiumFeatureIsEnabled(featuresStatus, DoppePremiumFeatures.hopp_remove_banner);
}